import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createUser, glogin } from "../../Api/Api";
import { signInWithGoogle } from "../../firebase";
import { FcGoogle } from "react-icons/fc";
import { FaUser, FaEnvelope, FaPhone, FaLock } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import authHeroImg from "../../Assets/og_technician_real.png";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email) errs.email = "Email is required";
    if (!form.mobile.match(/^\d{10}$/)) errs.mobile = "Enter valid 10-digit number";
    if (!form.password) errs.password = "Password is required";
    if (form.password !== form.confirmPassword) errs.confirmPassword = "Passwords do not match";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const { name, email, password, mobile } = form;
      await createUser({ name, email, password, mobile });

      toast.success("Registration successful! Redirecting...", { autoClose: 1500 });
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      console.error("Registration failed:", error);
      const msg = error?.response?.data?.error || "Registration failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithGoogle();
      const response = await glogin({
        g_id: result.user.uid,
        email: result.user.email,
        name: result.user.displayName,
      });

      localStorage.setItem("authToken", response.token);
      localStorage.setItem("user", JSON.stringify(response.user));

      toast.success(`Welcome, ${result.user.displayName}!`, { autoClose: 1500 });
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (error) {
      toast.error("Google login failed");
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <ToastContainer position="top-right" />

      {/* Main 2-Column Auth Card (Matching Screenshot Pixel-Neat) */}
      <div className="bg-white max-w-4xl w-full rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col md:flex-row my-6">
        
        {/* LEFT COLUMN: Lifestyle Image & Branding Overlay */}
        <div className="w-full md:w-1/2 relative bg-gray-900 overflow-hidden min-h-[350px] md:min-h-[550px] flex flex-col justify-end p-8 sm:p-10 shrink-0">
          <img
            src={authHeroImg}
            alt="Our Genie Technician"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

          {/* Overlaid Branding Text */}
          <div className="relative z-10 space-y-2 text-white">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Our Genie</h2>
            <p className="text-xs sm:text-sm text-gray-200 leading-relaxed max-w-sm">
              Professional home maintenance and concierge services at your fingertips.
            </p>
          </div>
        </div>

        {/* RIGHT COLUMN: Register Form Container */}
        <div className="w-full md:w-1/2 p-8 sm:p-10 md:p-12 flex flex-col justify-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0D004C] tracking-tight">
              Create Account
            </h1>
            <p className="text-xs text-gray-500 mt-1 mb-6">
              Join us to experience seamless home service management.
            </p>
          </div>

          {/* Google OAuth Button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full border border-gray-200 bg-white text-gray-700 font-bold text-xs py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition-all shadow-sm"
          >
            <FcGoogle size={20} />
            <span>Continue with Google</span>
          </button>

          {/* Divider */}
          <div className="relative my-6 flex items-center justify-center">
            <div className="border-t border-gray-200 w-full" />
            <span className="bg-white px-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest shrink-0 absolute">
              OR REGISTER WITH EMAIL
            </span>
          </div>

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-[11px] font-bold text-gray-700 mb-1">Full Name</label>
              <div className="relative">
                <FaUser className="absolute left-4 top-3.5 text-gray-400 text-xs" />
                <input
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C] bg-gray-50/30"
                />
              </div>
              {errors.name && <p className="text-[10px] text-red-500 mt-0.5">{errors.name}</p>}
            </div>

            {/* Email & Mobile 2-Column Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3.5 top-3.5 text-gray-400 text-xs" />
                  <input
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C] bg-gray-50/30"
                  />
                </div>
                {errors.email && <p className="text-[10px] text-red-500 mt-0.5">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-bold text-gray-700 mb-1">Mobile Number</label>
                <div className="relative">
                  <FaPhone className="absolute left-3.5 top-3.5 text-gray-400 text-xs" />
                  <input
                    type="text"
                    name="mobile"
                    placeholder="9876543210"
                    value={form.mobile}
                    onChange={handleChange}
                    className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C] bg-gray-50/30"
                  />
                </div>
                {errors.mobile && <p className="text-[10px] text-red-500 mt-0.5">{errors.mobile}</p>}
              </div>
            </div>

            {/* Password & Confirm Password 2-Column Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <FaLock className="absolute left-3.5 top-3.5 text-gray-400 text-xs" />
                  <input
                    type="password"
                    name="password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={handleChange}
                    className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C] bg-gray-50/30"
                  />
                </div>
                {errors.password && <p className="text-[10px] text-red-500 mt-0.5">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-[11px] font-bold text-gray-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <FaLock className="absolute left-3.5 top-3.5 text-gray-400 text-xs" />
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="••••••••"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-[#0D004C] bg-gray-50/30"
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-[10px] text-red-500 mt-0.5">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* Terms Disclaimer */}
            <p className="text-[10px] text-gray-400 text-center pt-1">
              By signing up, you agree to our <span className="font-bold text-gray-700">Terms</span> and <span className="font-bold text-gray-700">Privacy Policy</span>.
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0D004C] text-white py-3.5 rounded-xl font-bold text-xs hover:bg-[#16007A] transition-all shadow-md mt-2 flex items-center justify-center gap-2"
            >
              <span>{loading ? "Creating Account..." : "Create Account →"}</span>
            </button>
          </form>

          {/* Already have an account switch link */}
          <div className="mt-6 text-center text-xs text-gray-500 font-medium">
            Already have an account?{" "}
            <Link to="/login" className="font-bold text-[#0D004C] hover:underline">
              Login
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RegisterPage;
